import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Eye, Users, Clock, ShoppingCart, RefreshCw, MousePointerClick, Monitor, Smartphone, Tablet,
} from "lucide-react";
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { supabase } from "@/integrations/supabase/client";

const RANGE_DAYS = 30;

interface PageViewRow {
  path: string;
  session_id: string;
  user_id: string | null;
  device_type: string | null;
  duration_seconds: number;
  created_at: string;
}

interface DayPoint {
  date: string;
  views: number;
  visitors: number;
  orders: number;
}

interface PageStat {
  path: string;
  views: number;
  visitors: number;
  avgTime: number;
}

const DEVICE_COLORS: Record<string, string> = {
  mobile: "hsl(var(--primary))",
  desktop: "hsl(160 60% 45%)",
  tablet: "hsl(30 80% 55%)",
  unknown: "hsl(var(--muted-foreground))",
};

const fmtDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

const pageLabel = (path: string): string => {
  if (path === "/") return "Home";
  return path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .map((seg) => (seg.length > 24 ? `${seg.slice(0, 24)}…` : seg))
    .join(" / ");
};

export function SiteAnalytics() {
  const [loading, setLoading] = useState(true);
  const [pageViews, setPageViews] = useState<PageViewRow[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [ordersByDay, setOrdersByDay] = useState<Map<string, number>>(new Map());

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const since = new Date(Date.now() - RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString();

      const [viewsRes, ordersRes] = await Promise.all([
        supabase
          .from("page_views")
          .select("path, session_id, user_id, device_type, duration_seconds, created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(10000),
        supabase
          .from("orders")
          .select("id, created_at")
          .gte("created_at", since)
          .limit(5000),
      ]);

      setPageViews((viewsRes.data as PageViewRow[]) || []);

      const orders = ordersRes.data || [];
      setOrdersCount(orders.length);
      const byDay = new Map<string, number>();
      orders.forEach((o) => {
        const key = o.created_at.slice(0, 10);
        byDay.set(key, (byDay.get(key) || 0) + 1);
      });
      setOrdersByDay(byDay);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const stats = useMemo(() => {
    const sessions = new Set(pageViews.map((v) => v.session_id));
    const signedIn = new Set(pageViews.map((v) => v.user_id).filter(Boolean));
    const withTime = pageViews.filter((v) => v.duration_seconds > 0);
    const avgTime =
      withTime.length > 0
        ? withTime.reduce((s, v) => s + v.duration_seconds, 0) / withTime.length
        : 0;
    return {
      views: pageViews.length,
      visitors: sessions.size,
      signedInVisitors: signedIn.size,
      avgTime,
    };
  }, [pageViews]);

  const conversionRate = useMemo(() => {
    if (stats.visitors === 0) return "0.0";
    return ((ordersCount / stats.visitors) * 100).toFixed(1);
  }, [ordersCount, stats.visitors]);

  const trend = useMemo<DayPoint[]>(() => {
    const days: DayPoint[] = [];
    for (let i = RANGE_DAYS - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      days.push({ date: d.toISOString().slice(0, 10), views: 0, visitors: 0, orders: 0 });
    }
    const dayMap = new Map(days.map((d) => [d.date, d]));
    const sessionsPerDay = new Map<string, Set<string>>();
    pageViews.forEach((v) => {
      const key = v.created_at.slice(0, 10);
      const day = dayMap.get(key);
      if (!day) return;
      day.views += 1;
      if (!sessionsPerDay.has(key)) sessionsPerDay.set(key, new Set());
      sessionsPerDay.get(key)!.add(v.session_id);
    });
    sessionsPerDay.forEach((set, key) => {
      const day = dayMap.get(key);
      if (day) day.visitors = set.size;
    });
    ordersByDay.forEach((count, key) => {
      const day = dayMap.get(key);
      if (day) day.orders = count;
    });
    return days;
  }, [pageViews, ordersByDay]);

  const topPages = useMemo<PageStat[]>(() => {
    const map = new Map<string, { views: number; sessions: Set<string>; totalTime: number; timed: number }>();
    pageViews.forEach((v) => {
      const entry = map.get(v.path) || { views: 0, sessions: new Set(), totalTime: 0, timed: 0 };
      entry.views += 1;
      entry.sessions.add(v.session_id);
      if (v.duration_seconds > 0) {
        entry.totalTime += v.duration_seconds;
        entry.timed += 1;
      }
      map.set(v.path, entry);
    });
    return [...map.entries()]
      .map(([path, e]) => ({
        path,
        views: e.views,
        visitors: e.sessions.size,
        avgTime: e.timed > 0 ? e.totalTime / e.timed : 0,
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 12);
  }, [pageViews]);

  const deviceSplit = useMemo(() => {
    const map = new Map<string, number>();
    pageViews.forEach((v) => {
      const d = v.device_type || "unknown";
      map.set(d, (map.get(d) || 0) + 1);
    });
    return [...map.entries()].map(([name, value]) => ({ name, value }));
  }, [pageViews]);

  const statCards = [
    { label: "Page Views", value: stats.views.toLocaleString(), icon: Eye, color: "text-blue-600" },
    { label: "Unique Visitors", value: stats.visitors.toLocaleString(), icon: Users, color: "text-purple-600" },
    { label: "Avg. Time on Page", value: fmtDuration(stats.avgTime), icon: Clock, color: "text-teal-600" },
    { label: "Signed-In Visitors", value: stats.signedInVisitors.toLocaleString(), icon: MousePointerClick, color: "text-pink-600" },
    { label: "Orders", value: ordersCount.toLocaleString(), icon: ShoppingCart, color: "text-green-600" },
    { label: "Visitor → Order", value: `${conversionRate}%`, icon: ShoppingCart, color: "text-orange-600" },
  ];

  const deviceIcon = (name: string) => {
    if (name === "mobile") return Smartphone;
    if (name === "tablet") return Tablet;
    return Monitor;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Website Analytics</CardTitle>
              <CardDescription>Web traffic, pages and visitor behavior — last {RANGE_DAYS} days</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {statCards.map((stat) => (
              <Card key={stat.label}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                    <span className="text-xs font-medium text-muted-foreground">{stat.label}</span>
                  </div>
                  <div className="text-xl font-bold">{loading ? "..." : stat.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Traffic Trend</CardTitle>
            <CardDescription>Daily page views, unique visitors and orders</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => d.slice(5)}
                    tick={{ fontSize: 11 }}
                    className="text-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: 12,
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="views"
                    name="Page views"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary) / 0.2)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="visitors"
                    name="Visitors"
                    stroke="hsl(160 60% 45%)"
                    fill="hsl(160 60% 45% / 0.2)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    name="Orders"
                    stroke="hsl(30 80% 55%)"
                    fill="hsl(30 80% 55% / 0.2)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Devices</CardTitle>
            <CardDescription>How visitors browse the site</CardDescription>
          </CardHeader>
          <CardContent>
            {deviceSplit.length === 0 ? (
              <p className="text-sm text-muted-foreground py-12 text-center">No traffic recorded yet</p>
            ) : (
              <>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deviceSplit}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={75}
                      >
                        {deviceSplit.map((d) => (
                          <Cell key={d.name} fill={DEVICE_COLORS[d.name] || DEVICE_COLORS.unknown} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  {deviceSplit.map((d) => {
                    const Icon = deviceIcon(d.name);
                    return (
                      <div key={d.name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Icon className="h-3.5 w-3.5" />
                        <span className="capitalize">{d.name}</span>
                        <Badge variant="secondary" className="text-xs">{d.value}</Badge>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Pages</CardTitle>
          <CardDescription>Most visited pages and how long visitors stay</CardDescription>
        </CardHeader>
        <CardContent>
          {topPages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">
              No page views recorded yet — data appears as visitors browse the site.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Visitors</TableHead>
                  <TableHead className="text-right">Avg. Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topPages.map((page, i) => (
                  <TableRow key={page.path}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">#{i + 1}</Badge>
                        <span title={page.path}>{pageLabel(page.path)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{page.views.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{page.visitors.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      {page.avgTime > 0 ? fmtDuration(page.avgTime) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SiteAnalytics;
